import { Builder, Input } from '@builder.io/sdk';
import { AppDownloadWithForm, APP_DOWNLOAD_WITH_FORM_COMPONENT_NAME } from './AppDownloadWithForm';
import { appDownloadInputs } from '../AppDownload/AppDownload.registry';
import { landingPageFormInputs } from '../LandingPageForm/LandingPageForm.registry';
import { BuilderInputOptions } from '@/types/builder';

// helper function to process the inputs for AppDownloadWithForm
const getProcessedAppDownloadInputs = (): Input[] => {
  return appDownloadInputs.filter((input) => input.name !== 'headingLevel'); // heading level is determined by layout
};

// helper functions to process the inputs for LandingPageForm
const getProcessedLandingPageFormInputs = (): Input[] => {
  const filterExcludedFields = (inputs: Input[]): Input[] => {
    const excludedFields = ['heading', 'subHeading']; // not used in LPF in this unified component
    return inputs.filter((input) => !excludedFields.includes(input.name));
  };

  const overrideDefaults = (input: Input): Input => {
    if (input.name === 'showDurationPicker') {
      return {
        ...input,
        defaultValue: false,
      };
    }

    if (input.name === 'useCardStyle') {
      return {
        ...input,
        defaultValue: true,
      };
    }

    if (input.name === 'regLabel') {
      return {
        ...input,
        defaultValue: 'Enter Registration Number',
      };
    }

    if (input.name === 'submitButtonText') {
      return {
        ...input,
        defaultValue: 'Continue without discount',
      };
    }

    if (input.name === 'submitButtonColor') {
      return {
        ...input,
        defaultValue: 'purple',
      };
    }

    return input;
  };

  return filterExcludedFields(landingPageFormInputs).map(overrideDefaults);
};

const AppDownloadWithFormConfig = {
  name: APP_DOWNLOAD_WITH_FORM_COMPONENT_NAME,
  friendlyName: 'App Download with Form',
  inputs: [
    // Layout
    {
      name: 'layout',
      type: 'enum',
      friendlyName: 'Layout',
      helperText:
        'Default: app download heading + QR/badges on top, form below (heading is h1). Split: form section first with its own heading, then app download section with its own heading (both h2, page h1 lives elsewhere).',
      defaultValue: 'default',
      enum: [
        {
          label: 'Default (App download on top, form below)',
          value: 'default',
        },
        {
          label: 'Split (Form first, app download second, each with own heading)',
          value: 'split',
        },
      ],
    },
    // Background Section
    {
      name: 'backgroundImage',
      type: 'file',
      friendlyName: 'Background Image',
      helperText: 'Background image displayed behind the content on desktop only (hidden on mobile)',
      allowedFileTypes: ['png', 'jpg', 'jpeg', 'webp'],
      required: false,
    },
    {
      name: 'backgroundAlt',
      type: 'string',
      friendlyName: 'Background Image Alt Text',
      helperText: 'Alternative text for the background image for screen readers',
      defaultValue: 'Background image showing a curved road with a car driving on it',
      required: false,
      advanced: true,
    },
    // Form section headings (split layout only)
    {
      name: 'formHeading',
      type: 'string',
      friendlyName: 'Form Heading',
      helperText: 'Heading displayed above the form section (split layout only)',
      defaultValue: 'Get Tempcover now',
      required: false,
      showIf: (options: BuilderInputOptions) => options.get('layout') === 'split',
    },
    {
      name: 'formSubheading',
      type: 'string',
      friendlyName: 'Form Subheading',
      helperText: 'Subheading displayed below the form heading (split layout only)',
      defaultValue: "It's quick and easy - getting a quote takes less than 2 minutes",
      required: false,
      showIf: (options: BuilderInputOptions) => options.get('layout') === 'split',
    },
    ...getProcessedAppDownloadInputs(),
    ...getProcessedLandingPageFormInputs(),
  ] as Input[],
};

Builder.registerComponent(AppDownloadWithForm, AppDownloadWithFormConfig);
